import React, { useState, useEffect, useRef } from "react";
import { 
  Paper, Box, Stack, Typography, LinearProgress, CircularProgress,
  Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from "@mui/material";
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ReplayIcon from '@mui/icons-material/Replay';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';

export default function FloatingSaveBar({
  evaluatedStudentsCount,
  totalStudents,
  evaluationProgress,
  hasValidEvaluations,
  isSaving,
  saveFailed, 
  lessonDate,  
  Duration,   
  onSave,
  onLogout 
}) {
  const [timeLeft, setTimeLeft] = useState(null);
  const [hasAutoSaved, setHasAutoSaved] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({ open: false, type: '', message: '' });
  const [logoutTimer, setLogoutTimer] = useState(240); // 4 دقائق
  const prevIsSaving = useRef(isSaving);

  // --- نظام فحص البيانات القادمة من الصفحة الأم ---
  useEffect(() => {
    console.log("🕵️‍♂️ [FloatingSaveBar] البيانات المستلمة من الصفحة:", { 
      lessonDate: lessonDate, 
      Duration: Duration, 
    });
  }, [lessonDate, Duration]);

  // 1. حساب الوقت المتبقي لانتهاء الدرس
  useEffect(() => {
    const actualDuration = Duration;

    if (!lessonDate || actualDuration === undefined || actualDuration === null) {
      return; 
    }

    // إزالة حرف Z من النهاية ليتم احتساب الوقت محلياً وليس كـ UTC
    const localLessonDate = lessonDate.endsWith('Z') ? lessonDate.slice(0, -1) : lessonDate;
    const startTimeMs = new Date(localLessonDate).getTime();
    
    if (isNaN(startTimeMs)) return;

    const durationMs = Number(actualDuration) * 60 * 1000; 
    const targetEndTimeMs = startTimeMs + durationMs;

    const updateTimer = () => {
      const nowMs = Date.now();
      
      // إذا كان الوقت الحالي يسبق موعد بدء الدرس (الانتظار قبل الدرس)
      if (nowMs < startTimeMs) {
        setTimeLeft(Math.floor(durationMs / 1000));
        return;
      }

      const remainingMs = targetEndTimeMs - nowMs;

      // إذا انتهى الوقت الفعلي للدرس
      if (remainingMs <= 0) {
        setTimeLeft(0);
      } else {
        setTimeLeft(Math.floor(remainingMs / 1000));
      }
    };

    updateTimer(); 
    const timerId = setInterval(updateTimer, 1000);
    return () => clearInterval(timerId); 
  }, [lessonDate, Duration]);

  // 2. الإرسال التلقائي الصارم عند انتهاء الوقت
  useEffect(() => {
    if (timeLeft === 0 && !hasAutoSaved) {
      setHasAutoSaved(true);
      setDialogConfig({ 
        open: true, 
        type: 'timeUp', 
        message: 'انتهى وقت الدرس المخصص. جاري معالجة البيانات وإرسال التقييمات...' 
      });

      if (hasValidEvaluations && !isSaving && !saveFailed) {
        onSave();
      }
    }
  }, [timeLeft, hasValidEvaluations, isSaving, hasAutoSaved, saveFailed, onSave]);

  // 3. مراقبة حالة الحفظ
  useEffect(() => {
    if (prevIsSaving.current === true && isSaving === false) {
      if (saveFailed) {
        setDialogConfig({ open: true, type: 'error', message: 'حدث خطأ أثناء إرسال التقييمات. يرجى إعادة المحاولة.' });
      } else {
        setDialogConfig({ open: true, type: 'success', message: 'تم حفظ التقييمات بنجاح!' });
      }
    }
    prevIsSaving.current = isSaving;
  }, [isSaving, saveFailed]);

  // 4. عداد تسجيل الخروج التلقائي (بدون التنبيه الصوتي هنا)
  useEffect(() => {
    let logoutInterval;
    if (dialogConfig.open) {
      logoutInterval = setInterval(() => {
        setLogoutTimer((prev) => {
          if (prev <= 1) {
            clearInterval(logoutInterval);
            if (onLogout) onLogout(); 
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(logoutInterval);
  }, [dialogConfig.open, onLogout]);

  // 5. التنبيه الصوتي (يعمل لمدة دقيقة كاملة)
  const playAlertSound = () => {
    try {
      const audio = new Audio('https://actions.google.com/sounds/v1/alarms/siren_noise.ogg');
      audio.loop = true; 
      audio.play().catch(e => console.error("تنبيه: المتصفح يمنع الصوت", e));
      
      // إيقاف الصوت بعد 60 ثانية (دقيقة كاملة)
      setTimeout(() => { 
        audio.pause(); 
        audio.currentTime = 0; 
      }, 60000); 
    } catch (err) {}
  };

  // 6. تشغيل التنبيه في الدقيقة الأخيرة من وقت الدرس
  useEffect(() => {
    if (timeLeft === 60) {
      playAlertSound();
    }
  }, [timeLeft]);

  // 7. تنسيق الوقت
  const formatTime = (seconds) => {
    if (seconds === null) return "--:--";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  return (
    <>
      <Paper elevation={10} variant="glass" sx={{ position: "fixed", bottom: 24, left: "50%", p: "10px 15px", transform: "translateX(-50%)", width: "90%", maxWidth: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 10, borderRadius: 3, }}>
        <Box sx={{ flexGrow: 1, maxWidth: '65%', pr: 2 }}>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography variant="body2" fontWeight="bold" color="text.secondary">تم تقييم {evaluatedStudentsCount} من {totalStudents} طلاب</Typography>
            <Typography variant="body2" fontWeight="bold" color="text.secondary">{evaluationProgress}%</Typography>
          </Stack>
          <LinearProgress variant="determinate" value={evaluationProgress} color="secondary" sx={{ height: 8, borderRadius: 4 }} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: '130px', justifyContent: 'flex-end' }}>
          {isSaving ? (
            <>
              <CircularProgress size={20} color="warning" />
              <Typography variant="body2" color="warning.main" fontWeight="bold">جاري الإرسال...</Typography>
            </>
          ) : saveFailed ? (
            <Button variant="contained" color="error" size="small" startIcon={<ReplayIcon />} onClick={() => onSave()} sx={{ borderRadius: 2, fontWeight: 'bold' }}>فشل الإرسال - أعد المحاولة</Button>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" fontWeight="bold">الوقت المتبقي:</Typography>
              <Stack direction="row" alignItems="center" gap={0.5} sx={{ bgcolor: timeLeft !== null && timeLeft <= 60 ? 'error.light' : 'action.hover', px: 1.5, py: 0.5, borderRadius: 2 }}>
                <AccessTimeRoundedIcon fontSize="small" color={timeLeft !== null && timeLeft <= 60 ? "error.contrastText" : "primary"} />
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: timeLeft !== null && timeLeft <= 60 ? "error.contrastText" : "primary.main" }}>
                  {formatTime(timeLeft)}
                </Typography>
              </Stack>
            </>
          )}
        </Box>
      </Paper>

      <Dialog open={dialogConfig.open} disableEscapeKeyDown={true}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {dialogConfig.type === 'timeUp' && <WarningAmberRoundedIcon color="warning" />}
          {dialogConfig.type === 'success' && <CheckCircleOutlineRoundedIcon color="success" />}
          {dialogConfig.type === 'error' && <ErrorOutlineRoundedIcon color="error" />}
          <Typography variant="h6" fontWeight="bold">
            {dialogConfig.type === 'timeUp' ? 'تنبيه: انتهى وقت الدرس' : dialogConfig.type === 'success' ? 'عملية ناجحة' : 'خطأ في العملية'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2, fontWeight: 'bold', color: 'text.primary' }}>{dialogConfig.message}</DialogContentText>
          <Paper elevation={0} sx={{ bgcolor: 'error.light', p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="error.contrastText" fontWeight="bold">سيتم تسجيل الخروج تلقائياً لحماية بياناتك خلال:</Typography>
            <Typography variant="h6" color="error.contrastText" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatTime(logoutTimer)}</Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {dialogConfig.type === 'error' && <Button variant="outlined" color="error" onClick={() => onSave()} startIcon={<ReplayIcon />}>إعادة المحاولة</Button>}
          <Button variant="contained" color="error" onClick={() => { if(onLogout) onLogout(); }}>تسجيل الخروج الآن</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}